import { useQuery } from "@tanstack/react-query";
import apiClient from "./apiClient";
import { useAppContext } from "../libs/appContextLib";
import { getRecipesApiUrl } from "./recipesApiHelpers";

/**
 * Fetch recent executions across all recipes
 *
 * @param {Object} options - Optional filter parameters
 * @param {string} [options.status] - Filter by status: 'completed' | 'failed' | 'running'
 * @param {number} [options.limit] - Max results (default: 10)
 * @param {boolean} [options.enabled] - Enable/disable the query (default: true)
 * @returns {Object} React Query result with executions list
 */
export function useFetchRecentExecutions({ status = "completed", limit = 10, enabled = true } = {}) {
    const { currUser } = useAppContext();

    return useQuery({
        queryKey: [`${currUser?.customerId}_recent-executions`, status, limit],
        queryFn: async () => {
            const params = { limit };
            if (status) params.status = status;

            const response = await apiClient.get(
                `${getRecipesApiUrl()}/recipes/executions`,
                { params }
            );
            return response.data;
        },
        staleTime: 1000 * 60 * 2, // 2 minutes
        gcTime: 1000 * 60 * 15, // 15 minutes
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Don't refetch on every mount - use cached data
        enabled: !!currUser?.customerId && enabled, // Combine both conditions
    });
}
